import styled from "@emotion/styled";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Button } from "@livechat/design-system";
import { CreditCardOutline } from "@livechat/design-system-icons";
import { Icon, SpacingToken, Text } from "@livechat/design-system-react-components";

import { ActionLink } from "components/links/ActionLink";
import { trackEngagement } from "helpers/analytics";
import { BrowserHistory } from "services/browser-history";
import { IBilling } from "store/entities/billing/interfaces";
import { getCreditCardError } from "store/entities/credit-card";
import { withVar } from "themes/helpers";

import { SubscriptionCardForm } from "../components/SubscriptionCardForm/SubscriptionCardForm";

interface IProps {
  billing: IBilling | null;
  isSavingCreditCard: boolean;
  isCardFormVisible: boolean;
  onSubscriptionCardFromSubmitSuccess(tokenId: string): void;
  onDisplayCardForm(): void;
  onHideCardForm(): void;
}

export const AccountDetailsCreditCardSection: FC<IProps> = ({
  billing,
  onSubscriptionCardFromSubmitSuccess,
  isSavingCreditCard,
  isCardFormVisible,
  onDisplayCardForm,
  onHideCardForm,
}: IProps) => {
  const [previousIsSavingCreditCard, setPreviousIsSavingCreditCard] = useState(isSavingCreditCard);
  const billingDetailsLabel = billing?.billingInfo ? "Edit billing details" : "Add credit card";

  const creditCardError = useSelector(getCreditCardError);

  useEffect(() => {
    if (previousIsSavingCreditCard && !isSavingCreditCard && !creditCardError) {
      handleHideCardForm();
    }
    setPreviousIsSavingCreditCard(isSavingCreditCard);
  }, [isSavingCreditCard]);

  useEffect(() => {
    if (BrowserHistory.browserHistoryObject.location.hash === "#billing-details") {
      handleDisplayCardForm();
      BrowserHistory.removeHash();
    }
  }, [location.hash]);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getCardInfo = () => {
    return billing?.billingInfo?.creditCard
      ? `${billing.billingInfo.creditCard.cardType} **** ${billing.billingInfo.creditCard.lastFour}`
      : "No credit card added.";
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDisplayCardForm = () => {
    onDisplayCardForm();
    trackEngagement("Account details: Edit billing details");
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleHideCardForm = () => {
    onHideCardForm();
  };

  return (
    <StyledWrapper>
      {isCardFormVisible ? (
        <StyledFormWrapper>
          <SubscriptionCardForm
            billing={billing}
            onSubmitSuccess={onSubscriptionCardFromSubmitSuccess}
            onCancel={handleHideCardForm}
            isSaving={isSavingCreditCard}
            showButtons
            buttonsDisabled={isSavingCreditCard}
          />
        </StyledFormWrapper>
      ) : (
        <>
          <StyledCardInfoWrapper className="fs-mask">
            {billing ? (
              <>
                <Icon source={CreditCardOutline} />
                <Text bold>{getCardInfo()}</Text>
                <ActionLink data-testid="edit-account-details-button" onClick={handleDisplayCardForm} marginLeft="10px">
                  {billingDetailsLabel}
                </ActionLink>
              </>
            ) : (
              <Button kind="primary" onClick={handleDisplayCardForm} size="large">
                Add billing details
              </Button>
            )}
          </StyledCardInfoWrapper>
        </>
      )}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  margin-bottom: 28px;
  font-size: 14px;
`;

const StyledFormWrapper = styled.div`
  max-width: 600px;
`;

const StyledCardInfoWrapper = styled.div`
  display: flex;
  margin: 10px;
  justify-content: flex-start;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing3)};
`;
